import React from "react";
import { useFormContext } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { Button, Typography, IconButton } from "@material-tailwind/react";
import { Upload } from "iconoir-react";

import AppImages from "../assets/AppImages";
import { findInputError, isFormInvalid } from "../utils";
import { framer_error } from "../utils/inputValidations";

type FormFileUploadProps = {
    name: string;
    desc: string;
    accept: string;
    show_preview?: boolean;
    required?: string;
    title?: string;
    titleClass?: string;
    iconClass?: string;
    gap?: string;
    btnClass?: string;
    Icon?:  string | React.FC<React.SVGProps<SVGSVGElement>>;
    className?: string;
    imgClassName?: string;
    wrapperClassName?: string;
    minimal?: boolean;
};

const FormFileUpload: React.FC<FormFileUploadProps> = ({
    name,
    desc,
    accept,
    show_preview,
    required,
    title,
    titleClass,
    iconClass,
    gap,
    btnClass,
    Icon,
    className="",
    imgClassName="",
    wrapperClassName="",
    minimal=false,
}) => {
    const {
        register,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext();
    const inputErrors = findInputError(errors, name);
    const isInvalid = isFormInvalid(inputErrors);
    const file = watch(name);

    /*
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        setValue(name, files?.[0], { shouldValidate: true });
    };
    */

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const files = event.dataTransfer.files;
            setValue(name, files, { shouldValidate: true });
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    return (
        <div className={`h-full flex items-center flex-col gap-4`}>
            <div
                className={`justify-center h-full items-center flex flex-col transition-all ${gap}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragOver}
                onClick={(e: any) => {
                    e.currentTarget.parentElement?.querySelector(`#${name}`)?.click();
                }}
            >
                {file?.length ? (
                    <div className={`flex relative w-full h-full ${wrapperClassName}`}>
                        <IconButton
                            isCircular
                            size="sm"
                            variant="ghost"
                            className="!absolute top-2 right-2 z-10 bg-gray-400 text-white hover:bg-red-500"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                // Clear the file using react-hook-form setValue
                                setValue(name, null);
                            }}
                        >
                            <AppImages.iconTrash className="w-4 h-4" />
                        </IconButton>

                        {show_preview && ((typeof file === "string" && file) || file?.[0]?.type.includes("image")) ? (
                            <div className="relative w-full h-full">
                                <img
                                    src={typeof file === "string" ? file : URL.createObjectURL(file[0])}
                                    alt="File preview"
                                    className={`max-w-20 w-full h-full object-cover ${imgClassName}`}
                                />
                                <div className="absolute inset-0 bg-black/40 transition-opacity hover:opacity-100 opacity-0 flex items-center justify-center">
                                    <Typography className="text-white text-center font-medium">
                                        {typeof file === "string" ? file.split("/").pop() : file[0].name}
                                    </Typography>
                                </div>
                            </div>
                        ) : (
                            <div className={`flex items-center gap-2 p-2 bg-blue-gray-50 rounded-lg`}>
                                {Icon ? (
                                    typeof Icon === "string" ? (
                                        <img
                                            src={Icon}
                                            alt="File icon"
                                            className={`w-8 h-8 ${iconClass ?? ""}`}
                                        />
                                    ) : (
                                        <Icon className={`w-8 h-8 ${iconClass ?? ""}`} />
                                    )
                                ) : (
                                    <AppImages.iconPhotograph className={`text-[#64748B] w-5 h-5 ${iconClass}`} />
                                )}
                                <Typography className="text-center text-black overflow-hidden text-ellipsis">
                                    {file[0].name}
                                </Typography>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={`flex flex-row items-center gap-2 ${className}`}>
                        <div className={`bg-secondary rounded-full p-4 ${minimal ? "hidden" : ""}`}>
                            {Icon ? (
                                typeof Icon === "string" ? (
                                    <img
                                        src={Icon}
                                        alt="File icon"
                                        className={`w-8 h-8 ${iconClass ?? ""}`}
                                    />
                                ) : (
                                    <Icon className={`w-8 h-8 ${iconClass ?? ""}`} />
                                )
                            ) : (
                                <AppImages.iconPhotograph
                                    className={`text-[#64748B] w-8 h-8 ${iconClass}`}
                                />
                            )}
                        </div>
                        <Typography className={`block text-primary ${titleClass}`}>
                            {title}
                        </Typography>
                        {desc && (
                            <Typography type="small" className="block text-foreground">
                                {desc}
                            </Typography>
                        )}
                        <Button
                            variant="outline"
                            className={`flex w-max items-center gap-2 justify-center ${btnClass}`}
                        >
                            <Upload className="w-5 h-5" />
                            Choose File
                        </Button>
                    </div>
                )}
            </div>

            {required ? (
                <input
                    type="file"
                    id={name}
                    {...register(name, { required })}
                    className="hidden"
                    accept={accept}
                />
            ) : (
                <input
                    type="file"
                    id={name}
                    {...register(name)}
                    className="hidden"
                    accept={accept}
                />
            )}

            <div className="flex justify-between">
                <AnimatePresence mode="wait" initial={false}>
                    {isInvalid && (
                        <motion.p
                            className="flex items-center gap-1 py-1 mb-1 px-2 font-semibold text-red-500 bg-red-100 rounded-md"
                            {...framer_error}
                        >
                            {inputErrors.error.message}
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default FormFileUpload;
